import React, { useState } from 'react';

import { observer } from 'mobx-react';
import { Grid, ModalProps } from '@mui/material';
import { useRouteLoaderData } from 'react-router-dom';

import {
  AppCampCollectionFragment,
  CampCollectionDocument,
  CampCollections,
  useAddCampCollectionItemsMutation,
  useCampCollectionQuery,
  useUpdateCampMutation,
  CampColumnsDocument,
  CampQuery,
} from '../../../generated/graphql';
import { useModalRoute } from '../../../hooks';
import { CacheOperation, updateCache } from '../../../utils/updateCache';
import { StyledRoleBox } from '../../item-card';
import {
  ActiveOnCampSwitch,
  CampCollectionForm,
  CampCollectionModalContainer,
  ImportFromCamp,
  StyledItemListContainer,
} from '../common';

const NEW_ITEM_NAME = 'Nytt boende';
const collection = CampCollections.Livings;
const TITLE = 'Boende';
const ITEM_SINGULAR = 'Boende';

export const CampLivingsModal = observer(function CampLivingsModal({
  open,
  onClose,
  ...props
}: Omit<ModalProps, 'children'>) {
  const [selectedItem, setSelectedItem] = useState<AppCampCollectionFragment>();

  const camp = useRouteLoaderData('camp') as CampQuery['camp'];
  const [activeValue, setActiveValue] = useState(camp.livings_active!);
  const [addItems] = useAddCampCollectionItemsMutation();

  const [updateCamp] = useUpdateCampMutation();

  const addItem = () => {
    addItems({
      variables: {
        collection,
        input: [
          {
            name: NEW_ITEM_NAME,
            camp_id: camp.camp_id,
          },
        ],
      },
      update: (
        proxy: any,
        result: { data: { addCampCollectionItems: any } },
      ) => {
        updateCache({
          method: CacheOperation.CREATE,
          query: CampCollectionDocument,
          proxy,
          mutationResultObject: result.data.addCampCollectionItems,
          variables: {
            camp_id: camp.camp_id,
            collection,
          },
        });
      },
    }).then(({ data }: { data: any }) => {
      setSelectedItem(data.addCampCollectionItems[0]);
    });
  };

  const importList = <ImportFromCamp collection={collection} />;
  const itemList = <ItemList setSelectedItem={setSelectedItem} />;
  const itemForm = (
    <CampCollectionForm
      descriptionEnabled={false}
      priceEnabled={true}
      limitEnabled={true}
      genderEnabled={true}
      itemSingular={ITEM_SINGULAR}
      selectedItem={selectedItem!}
      setSelectedItem={setSelectedItem}
    />
  );

  return (
    <CampCollectionModalContainer
      {...{
        addItem,
        open,
        onClose,
        title: TITLE,
        importList,
        itemList,
        itemForm,
        maxWidth: 'lg',
      }}
    >
      <ActiveOnCampSwitch
        checked={activeValue}
        onChange={(e, checked) => {
          setActiveValue(checked);
          updateCamp({
            variables: {
              body: {
                camp_id: camp.camp_id,
                livings_active: checked,
              },
            },
            refetchQueries: [
              {
                query: CampColumnsDocument,
                variables: { camp_id: camp.camp_id },
              },
            ],
          });
        }}
      />
    </CampCollectionModalContainer>
  );
});

const ItemList = observer(function ItemList({
  setSelectedItem,
}: {
  setSelectedItem: any;
}) {
  const camp = useRouteLoaderData('camp') as CampQuery['camp'];

  const { data } = useCampCollectionQuery({
    variables: {
      collection,
      camp_id: camp.camp_id,
    },
  });

  return (
    <StyledItemListContainer className="no_scrollbar ">
      <Grid container wrap={'wrap'} spacing={3}>
        {data &&
          data.campCollection.map(
            (item: { id: React.Key | null | undefined }) => (
              <Item
                key={item.id}
                item={item}
                setSelectedItem={setSelectedItem}
              />
            ),
          )}
      </Grid>
    </StyledItemListContainer>
  );
});

const Item = observer(function Item({
  item,
  setSelectedItem,
}: {
  item: any;
  setSelectedItem: any;
}) {
  return (
    <Grid item xs={12} sm={4} md={4}>
      <StyledRoleBox
        onClick={() => {
          setSelectedItem(item);
        }}
        success={item.deleted ? false : true}
        error={item.deleted ? true : false}
      >
        {item.name}
      </StyledRoleBox>
    </Grid>
  );
});
